<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/exams/edit/${examId}`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
        <button class="btn btn-primary btn-sm" @click="showAddModal = true">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Question
        </button>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">{{ exam.name }} - Questions</h6>
      </div>
      <div class="card-body">
        <div v-if="questions.length === 0" class="text-center py-5 text-muted">
          <vue-feather type="help-circle" size="48" class="mb-3 opacity-50" />
          <p class="mb-0">No questions added yet. Click "Add Question" to get started.</p>
        </div>

        <div v-else class="questions-list">
          <div v-for="(question, index) in questions" :key="question.id" class="question-item card mb-3">
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <h6 class="mb-2">Q{{ index + 1 }}. {{ question.text }}</h6>
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-primary" @click="editQuestion(question)">
                    <vue-feather type="edit" size="14" />
                  </button>
                  <button class="btn btn-outline-danger" @click="deleteQuestion(question)">
                    <vue-feather type="trash-2" size="14" />
                  </button>
                </div>
              </div>
              <div class="options mt-2">
                <div v-for="(option, i) in question.options" :key="i" class="option-item">
                  <span :class="['badge me-2', option.isCorrect ? 'bg-success' : 'bg-secondary']">
                    {{ String.fromCharCode(65 + i) }}
                  </span>
                  {{ option.text }}
                </div>
              </div>
              <div class="mt-2">
                <small class="text-muted">Points: {{ question.points }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Question Modal -->
    <div v-if="showAddModal" class="modal show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingQuestion ? 'Edit' : 'Add' }} Question</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Question <span class="text-danger">*</span></label>
              <textarea v-model="questionForm.text" class="form-control" rows="2" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Question Type</label>
              <select v-model="questionForm.type" class="form-select">
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True/False</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Options</label>
              <div v-for="(option, index) in questionForm.options" :key="index" class="input-group mb-2">
                <span class="input-group-text">{{ String.fromCharCode(65 + index) }}</span>
                <input v-model="option.text" type="text" class="form-control" placeholder="Option text" />
                <div class="input-group-text">
                  <input
                    v-model="option.isCorrect"
                    type="checkbox"
                    class="form-check-input mt-0"
                    @change="handleCorrectChange(index)"
                  />
                  <label class="ms-1 mb-0">Correct</label>
                </div>
                <button
                  v-if="questionForm.options.length > 2"
                  class="btn btn-outline-danger"
                  @click="removeOption(index)"
                >
                  <vue-feather type="x" size="14" />
                </button>
              </div>
              <button class="btn btn-outline-secondary btn-sm" @click="addOption">
                <vue-feather type="plus" size="14" class="me-1" />
                Add Option
              </button>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Points</label>
                <input v-model.number="questionForm.points" type="number" class="form-control" min="1" />
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Explanation (shown after answer)</label>
              <textarea v-model="questionForm.explanation" class="form-control" rows="2"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="closeModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveQuestion" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ editingQuestion ? 'Update' : 'Add' }} Question
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAddModal" class="modal-backdrop show"></div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'ExamQuestions',
  setup() {
    const route = useRoute()
    const alert = useAlert()
    const examId = route.params.id

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', url: '/app/exams/list' },
      { title: 'Questions', active: true }
    ]

    const exam = ref({ name: '' })
    const questions = ref([])
    const showAddModal = ref(false)
    const editingQuestion = ref(null)
    const saving = ref(false)

    const defaultQuestionForm = () => ({
      text: '',
      type: 'multiple_choice',
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ],
      points: 1,
      explanation: ''
    })

    const questionForm = ref(defaultQuestionForm())

    const fetchExam = async () => {
      try {
        const response = await api.get(`/v1/admin/exams/${examId}`)
        exam.value = response.data.exam
        questions.value = response.data.exam.questions || []
      } catch (error) {
        console.error('Failed to fetch exam:', error)
      }
    }

    const addOption = () => {
      questionForm.value.options.push({ text: '', isCorrect: false })
    }

    const removeOption = (index) => {
      questionForm.value.options.splice(index, 1)
    }

    const handleCorrectChange = (index) => {
      if (questionForm.value.type === 'multiple_choice') {
        questionForm.value.options.forEach((opt, i) => {
          if (i !== index) opt.isCorrect = false
        })
      }
    }

    const editQuestion = (question) => {
      editingQuestion.value = question
      questionForm.value = { ...question, options: [...question.options] }
      showAddModal.value = true
    }

    const closeModal = () => {
      showAddModal.value = false
      editingQuestion.value = null
      questionForm.value = defaultQuestionForm()
    }

    const saveQuestion = async () => {
      if (!questionForm.value.text || !questionForm.value.options.some(o => o.isCorrect)) {
        alert.error('Error', 'Please fill in the question and mark at least one correct answer')
        return
      }

      saving.value = true
      try {
        if (editingQuestion.value) {
          await api.put(`/v1/admin/exams/${examId}/questions/${editingQuestion.value.id}`, questionForm.value)
          const index = questions.value.findIndex(q => q.id === editingQuestion.value.id)
          questions.value[index] = { ...questionForm.value, id: editingQuestion.value.id }
        } else {
          const response = await api.post(`/v1/admin/exams/${examId}/questions`, questionForm.value)
          questions.value.push(response.data.question)
        }
        closeModal()
      } catch (error) {
        alert.error('Error', 'Failed to save question')
      } finally {
        saving.value = false
      }
    }

    const deleteQuestion = async (question) => {
      const result = await alert.delete(
        'Delete Question?',
        'This action cannot be undone.'
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/exams/${examId}/questions/${question.id}`)
          questions.value = questions.value.filter(q => q.id !== question.id)
        } catch (error) {
          alert.error('Error', 'Failed to delete question')
        }
      }
    }

    onMounted(fetchExam)

    return {
      examId,
      breadcrumbs,
      exam,
      questions,
      showAddModal,
      editingQuestion,
      questionForm,
      saving,
      addOption,
      removeOption,
      handleCorrectChange,
      editQuestion,
      closeModal,
      saveQuestion,
      deleteQuestion
    }
  }
}
</script>

<style scoped>
.option-item {
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 4px;
}
</style>
